// Шторка сессии: статус, перенос, напоминание, правка, наличные, удаление. У резерва — подтвердить или снять.
import { MEET, RU_DAYS, STATUS, applyRule, dowOf, effectiveRules, hmToMin, isCommercial, money, ruDate, ruDateShort,
  type SessionOut, type Status } from '@praktika/core';
import { afterChange, call, reload } from '../lib/api.ts';
import { $, $$, esc } from '../lib/dom.ts';
import { clientById, groupLabel, groupOf, meetLabel, meetShort, sessionById, statusLabel, typeName } from '../lib/data.ts';
import { go, render } from '../lib/nav.ts';
import { IC, closeSheet, openSheet, opt } from '../lib/sheet.ts';
import { S } from '../lib/state.ts';
import { nowInfo } from '../lib/zones.ts';
import { pickClient, remind } from './common.ts';
import { cashSheet } from './money.ts';

// Варианты того же резерва: пакет (заведены вместе) или, у старых без пакета, все резервы клиента.
export function reservePack(s: SessionOut): SessionOut[] {
  return S.sessions.filter((x) => x.status === STATUS.RESERVE && x.clientId === s.clientId &&
    (s.pack ? x.pack === s.pack : !x.pack)).sort((a, b) => (a.date + a.start) < (b.date + b.start) ? -1 : 1);
}

function reserveSheet(s: SessionOut): void {
  const c = clientById(s.clientId), pack = reservePack(s), k = pack.findIndex((x) => x.id === s.id) + 1;
  const sh = openSheet('<div class="ttl">' + esc(s.clientName) + ' · ' + esc(RU_DAYS[dowOf(s.date)]!.toLowerCase() + ' ' +
    ruDateShort(s.date)) + ' · ' + esc(s.start) + '–' + esc(s.end) + '</div><p class="sub"><b>Резерв</b>' +
    (pack.length > 1 ? ' · вариант ' + k + ' из ' + pack.length : '') + ' · ' + esc(typeName(s.typeId)) + ' ' + esc(s.dur) +
    ' · время держится за клиентом, это ещё не сессия</p>' +
    opt('ok', IC.ok, 'Подтвердить', pack.length > 1 ? 'станет сессией; про остальные варианты спрошу' : 'станет сессией') +
    opt('no', IC.x, 'Снять резерв', 'время освободится') + '<div class="gap"></div>' +
    opt('', IC.cal, 'Перенести') + opt('', IC.chat, 'Написать' + (c && c.messenger ? ' в ' + c.messenger : '')) +
    opt('', IC.edit, 'Изменить: тип, цену, ссылку'));
  const acts = [() => confirmReserve(s.id), () => dropReserve(s), () => go('move', { id: s.id }), () => remind(s.id),
    () => go('editSession', { id: s.id })];
  $$('button.opt', sh).forEach((b, i) => { b.onclick = () => { closeSheet(); acts[i]!(); }; });
}

export function dropReserve(s: SessionOut | null): void {
  if (!s || !confirm('Снять резерв ' + s.clientName + ' ' + ruDateShort(s.date) + ' ' + s.start + '?')) return;
  void call('deleteSession', s.id).then(() => afterChange('Резерв снят'), () => {});
}

// Подтвердить вариант: он становится сессией, про остальные варианты пакета — спросить (решает психолог).
export function confirmReserve(id: string): void {
  const s = sessionById(id);
  if (!s) return;
  const others = reservePack(s).filter((x) => x.id !== id);
  void call('setStatus', id, STATUS.PLAN).then(async () => {
    if (!others.length) return afterChange('Подтверждено: ' + ruDateShort(s.date) + ' ' + s.start);
    await reload();
    const keep: Record<string, boolean> = {};
    const sh = openSheet('<div class="ttl">Подтверждено: ' + esc(ruDateShort(s.date) + ' ' + s.start) + '</div>' +
      '<p class="sub">' + esc(s.clientName) + ': остальные варианты — удалить? Время освободится.</p>' +
      others.map((o) => '<button class="kv" data-o="' + esc(o.id) + '"><span class="k">' +
        esc(RU_DAYS[dowOf(o.date)] + ' ' + ruDateShort(o.date) + ' · ' + o.start + '–' + o.end) +
        '</span><span class="tog on"></span></button>').join('') + '<div class="btnrow" style="padding:12px 0 0">' +
      '<button class="btn pri" data-dels>Удалить отмеченные</button><button class="btn sec" data-keep>Оставить</button></div>');
    $$('[data-o]', sh).forEach((b) => {
      b.onclick = () => {
        const oid = b.getAttribute('data-o')!;
        keep[oid] = !keep[oid];
        $('.tog', b).classList.toggle('on', !keep[oid]);
      };
    });
    $('[data-keep]', sh).onclick = () => { closeSheet(); render(); };
    $('[data-dels]', sh).onclick = async () => {
      closeSheet();
      const del = others.filter((o) => !keep[o.id]);
      try { for (const o of del) await call('deleteSession', o.id); } catch { /* сообщение уже показано */ }
      void afterChange('Удалено вариантов: ' + del.length);
    };
  }, () => {});
}

const STATUS_BUTTONS: Status[] = [STATUS.DONE, STATUS.CANCEL_OK, STATUS.CANCEL_LATE, STATUS.NO_SHOW, STATUS.CANCEL_PSY];

export function sessionSheet(id: string): void {
  const s = sessionById(id);
  if (!s) return;
  if (s.status === STATUS.RESERVE) { reserveSheet(s); return; }
  // кто платит и правила отмены — по группе самой сессии, а не по нынешней карточке клиента
  const c = clientById(s.clientId), g = groupOf(s), rules = effectiveRules(c, g, S.settings);
  const payer = isCommercial(g) ? { id: g!.id, name: g!.name, isProject: true } :
    (c ? { id: c.id, name: c.name, isProject: false } : null);
  const price = money(s.price, s.cur), acct = payer ? (payer.isProject ? 'со счёта «' + payer.name + '»' :
    'со счёта клиента') : '';
  const p = s.price ?? 0, lateAmt = applyRule(rules.late, p), noAmt = applyRule(rules.noshow, p),
    psyAmt = applyRule(rules.psy, p);
  const ruleSrc = rules.source === 'свои' ? 'свои правила' : rules.source === 'по умолчанию' ? 'правила по умолчанию' :
    'правила «' + rules.source.replace(/^группы /, '') + '»';
  let html = '<div class="ttl">' + esc(s.clientName) + ' · ' + esc(s.start) + '–' + esc(s.end) +
    '</div><p class="sub">' + esc(groupLabel(s)) + ' · ' + esc(typeName(s.typeId)) + ' ' + esc(s.dur) + ' · ' +
    esc(meetLabel(s)) + (s.status !== STATUS.PLAN ? ' · <b>' + esc(statusLabel(s.status)) + '</b>' : '') + '</p>';
  if (s.link && s.meet !== MEET.OFFLINE) html += '<a class="btn pri" style="margin-bottom:6px" href="' + esc(s.link) +
    '" target="_blank" rel="noopener">' + IC.video + 'Войти в ' + esc(meetShort(s)) + '</a>';
  html += opt('ok', IC.ok, 'Прошла', 'списать ' + price + ' ' + acct) +
    opt('', IC.clock, 'Отменена вовремя', 'раньше чем за ' + rules.hours + ' ч, без оплаты') +
    opt('late', IC.warn, 'Отменена поздно', ruleSrc + ': ' + (lateAmt ? money(lateAmt, s.cur) : 'без оплаты')) +
    opt('no', IC.x, 'Не пришёл', ruleSrc + ': ' + (noAmt ? money(noAmt, s.cur) : 'без оплаты')) +
    opt('', IC.psy, 'Отменил психолог', psyAmt ? money(psyAmt, s.cur) : 'без оплаты') +
    (s.status !== STATUS.PLAN ? opt('', IC.cal, 'Снова «Запланирована»', '') : '') +
    '<div class="gap"></div>' + opt('', IC.cal, 'Перенести') +
    opt('', IC.chat, 'Написать' + (c && c.messenger ? ' в ' + c.messenger : '')) +
    opt('', IC.edit, 'Изменить: тип, цену, ссылку') +
    opt('', IC.psy, 'Это другой клиент', 'перенести сессию к нему: цена и начисление — по его карточке') +
    opt('', IC.card, 'Оплата наличными') + opt('no', IC.trash, 'Удалить сессию');
  const sh = openSheet(html);
  const setSt = (st: Status) => {
    const nw = nowInfo(), ahead = s.date > nw.date || (s.date === nw.date && (hmToMin(s.start) ?? 0) + s.dur > nw.min);
    if ((st === STATUS.DONE || st === STATUS.NO_SHOW) && ahead && !confirm('Сессия ещё не закончилась (' +
      ruDate(s.date) + ', ' + s.start + '–' + s.end + '). Всё равно отметить «' + statusLabel(st) + '»?')) return;
    closeSheet();
    void call<{ charged: number }>('setStatus', id, st).then((r) => afterChange(statusLabel(st) +
      (r.charged ? ' · начислено ' + money(r.charged, s.cur) : '')), () => {});
  };
  const acts: Array<() => void> = STATUS_BUTTONS.map((st) => () => setSt(st));
  if (s.status !== STATUS.PLAN) acts.push(() => setSt(STATUS.PLAN));
  acts.push(
    () => { closeSheet(); go('move', { id }); },
    () => { closeSheet(); remind(id); },
    () => { closeSheet(); go('editSession', { id }); },
    () => {
      closeSheet();
      pickClient((cid) => {
        if (cid === s.clientId) return;
        void call<{ message: string }>('reassignSession', id, cid).then((r) => afterChange(r.message), () => {});
      });
    },
    () => { closeSheet(); cashSheet(payer ? payer.id : ''); },
    () => {
      if (!confirm('Удалить сессию ' + s.clientName + ' ' + ruDateShort(s.date) + ' ' + s.start + '?')) return;
      closeSheet();
      void call('deleteSession', id).then(() => afterChange('Сессия удалена'), () => {});
    });
  $$('button.opt', sh).forEach((b, i) => { b.onclick = () => acts[i]!(); });
}
